import { useMemo } from "react"
import { Box, Button, Container, Typography, Stack } from "@mui/material"
import { EmojiEvents, Replay, Groups, PlayArrow } from "@mui/icons-material"
import { keyframes } from "@mui/system"

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.04); }
`

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 30px rgba(255,255,255,0.12); }
  50% { box-shadow: 0 0 50px rgba(255,215,0,0.35); }
`

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`

const shine = keyframes`
  0% { opacity: 0; transform: translateX(-100%) skewX(-15deg); }
  100% { opacity: 1; transform: translateX(100%) skewX(-15deg); }
`

const flare = keyframes`
  0%, 100% { opacity: 0.3; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.1); }
`

const podiumData = [
  { rank: 5, name: "ירושלים", score: 3120, color: "#7a4dff" },
  { rank: 4, name: "חיפה", score: 4560, color: "#7a4dff" },
  { rank: 1, name: "בני ברק", score: 12450, color: "#ffcb3d", highlight: true },
  { rank: 3, name: "ראש העין", score: 6890, color: "#ff6b3e" },
  { rank: 2, name: "תל אביב", score: 9230, color: "#40c4ff" },
]

const PodiumCard = ({ rank, name, score, color, highlight }) => {
  const size = highlight ? { xs: 220, md: 280 } : { xs: 160, md: 210 }
  const topOffset = highlight ? -24 : 0

  return (
    <Box
      sx={{
        position: "relative",
        width: size,
        minWidth: size,
        pt: highlight ? 4 : 2,
        pb: 2,
        px: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        borderRadius: 4,
        background: highlight
          ? "radial-gradient(circle at top, rgba(255,223,94,0.42), rgba(30,22,11,0.75))"
          : "linear-gradient(180deg, rgba(12,24,52,0.98), rgba(13,27,55,0.88))",
        border: `1px solid ${highlight ? "rgba(255,193,7,0.55)" : "rgba(255,255,255,0.12)"}`,
        boxShadow: highlight
          ? "0 28px 80px rgba(255,182,72,0.2), inset 0 0 18px rgba(255,216,122,0.14)"
          : "0 18px 40px rgba(0,0,0,0.24), inset 0 0 8px rgba(255,255,255,0.05)",
        backdropFilter: "blur(8px)",
        zIndex: highlight ? 3 : 1,
        transform: highlight ? "translateY(-18px)" : "none",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: topOffset,
          left: "50%",
          transform: "translateX(-50%)",
          width: highlight ? 72 : 56,
          height: highlight ? 72 : 56,
          borderRadius: "50%",
          background: `radial-gradient(circle at 30% 30%, rgba(255,255,255,0.95), ${color}44 40%, transparent 80%)`,
          border: `2px solid ${highlight ? "rgba(255,235,150,0.9)" : "rgba(255,255,255,0.2)"}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: highlight ? "#2b1700" : "#e7f3ff",
          fontWeight: 900,
          fontSize: highlight ? "1.6rem" : "1.2rem",
          boxShadow: highlight ? "0 0 30px rgba(255,193,7,0.45)" : "0 0 14px rgba(0,154,255,0.18)",
          animation: highlight ? `${pulse} 2.5s ease-in-out infinite` : "none",
        }}
      >
        {rank}
      </Box>

      <Box sx={{ mt: highlight ? 4 : 3, width: "100%" }}>
        <Typography
          sx={{
            color: highlight ? "#fff" : "#d9e9ff",
            fontWeight: 900,
            fontSize: highlight ? { xs: "1.45rem", md: "1.8rem" } : { xs: "1rem", md: "1.2rem" },
            letterSpacing: "0.02em",
            textShadow: highlight ? "0 0 18px rgba(255,215,110,0.5)" : "0 0 12px rgba(0,0,0,0.35)",
          }}
        >
          {name}
        </Typography>
        <Typography
          sx={{
            color: highlight ? "#ffefb0" : "rgba(255,255,255,0.68)",
            fontWeight: 700,
            mt: 1,
            fontSize: { xs: "0.92rem", md: "1rem" },
          }}
        >
          נקודות
        </Typography>
        <Typography
          sx={{
            mt: 1.5,
            fontWeight: 900,
            fontSize: highlight ? { xs: "2rem", md: "2.8rem" } : { xs: "1.4rem", md: "1.8rem" },
            color: highlight ? "#fff" : "#c7e0ff",
            letterSpacing: "0.02em",
            textShadow: "0 4px 20px rgba(0,0,0,0.35)",
          }}
        >
          {score.toLocaleString()}
        </Typography>
      </Box>
    </Box>
  )
}

export default function ADRTU() {
  const podium = useMemo(() => podiumData, [])

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        overflow: "hidden",
        backgroundColor: "#060814",
        backgroundImage: `radial-gradient(circle at top, rgba(56, 106, 255, 0.08), transparent 30%), radial-gradient(circle at 20% 10%, rgba(255, 192, 0, 0.18), transparent 16%), radial-gradient(circle at 80% 20%, rgba(131, 67, 255, 0.16), transparent 18%), linear-gradient(180deg, #091126 0%, #05080f 45%, #02050b 100%)`,
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: { xs: 4, md: 6 },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: `radial-gradient(circle at 15% 20%, rgba(255, 255, 255, 0.15), transparent 10%), radial-gradient(circle at 85% 10%, rgba(255, 255, 255, 0.1), transparent 8%)`,
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: `linear-gradient(135deg, rgba(255,255,255,0.03) 0%, transparent 45%, rgba(255,255,255,0.02) 100%)`,
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: "18%",
          left: "50%",
          transform: "translateX(-50%)",
          width: "92%",
          height: "92%",
          borderRadius: "50%",
          border: "1px solid rgba(255,255,255,0.04)",
          boxShadow: "0 0 120px rgba(96, 175, 255, 0.06)",
          pointerEvents: "none",
        }}
      />
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
        <Box sx={{ textAlign: "center", mb: { xs: 4, md: 5 } }}>
          <Box
            sx={{
              mx: "auto",
              width: { xs: 74, md: 98 },
              height: { xs: 74, md: 98 },
              borderRadius: "50%",
              background: "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.95), rgba(255,193,7,0.3) 32%, transparent 78%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 0 30px rgba(255,193,7,0.35)",
              animation: `${pulse} 2.8s ease-in-out infinite`,
              mb: 2,
            }}
          >
            <EmojiEvents sx={{ fontSize: { xs: 38, md: 46 }, color: "#fff" }} />
          </Box>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 900,
              fontSize: { xs: "2.3rem", md: "3.8rem" },
              lineHeight: 1.02,
              letterSpacing: "0.08em",
              color: "#fff",
              textShadow: "0 12px 30px rgba(0,0,0,0.5)",
              mb: 1,
            }}
          >
            דירוג הקבוצות
          </Typography>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: { xs: "1rem", md: "1.4rem" },
              color: "rgba(255,255,255,0.72)",
              letterSpacing: "0.16em",
            }}
          >
            קליקרים
          </Typography>
        </Box>

        <Box sx={{ position: "relative", mb: { xs: 4, md: 6 } }}>
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              opacity: 0.25,
              backgroundImage: `radial-gradient(circle at 10% 20%, rgba(255,255,255,0.22), transparent 16%), radial-gradient(circle at 90% 16%, rgba(255,255,255,0.14), transparent 12%)`,
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-end",
              gap: { xs: 1.3, md: 2.5 },
              flexWrap: "wrap",
              px: { xs: 0.5, md: 1 },
            }}
          >
            {podium.map((item) => (
              <PodiumCard key={item.rank} {...item} />
            ))}
          </Box>
        </Box>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="center"
          alignItems="center"
          sx={{ mt: 2 }}
        >
          <Button
            variant="contained"
            startIcon={<Replay />}
            sx={{
              minWidth: 190,
              px: 4,
              py: 1.6,
              borderRadius: 99,
              fontWeight: 800,
              fontSize: "0.99rem",
              textTransform: "none",
              color: "#fff",
              background: "linear-gradient(135deg, rgba(255,255,255,0.12), rgba(255,255,255,0.06))",
              border: "1px solid rgba(255,255,255,0.14)",
              boxShadow: "0 18px 45px rgba(0,0,0,0.24)",
              backdropFilter: "blur(14px)",
              animation: `${glow} 3s infinite`,
              '&:hover': {
                background: "linear-gradient(135deg, rgba(255,255,255,0.18), rgba(255,255,255,0.08))",
              },
            }}
          >
            חזור שוב
          </Button>
          <Button
            variant="contained"
            startIcon={<Groups />}
            sx={{
              minWidth: 190,
              px: 4,
              py: 1.6,
              borderRadius: 99,
              fontWeight: 800,
              fontSize: "0.99rem",
              textTransform: "none",
              color: "#fff",
              background: "linear-gradient(135deg, rgba(20, 95, 212, 0.96), rgba(66, 213, 255, 0.95))",
              boxShadow: "0 18px 45px rgba(26, 132, 255, 0.3)",
              '&:hover': {
                transform: "translateY(-2px)",
                boxShadow: "0 24px 55px rgba(26, 132, 255, 0.4)",
              },
            }}
          >
            חזרה לשחקנים
          </Button>
          <Button
            variant="contained"
            startIcon={<PlayArrow />}
            sx={{
              minWidth: 190,
              px: 4,
              py: 1.6,
              borderRadius: 99,
              fontWeight: 800,
              fontSize: "0.99rem",
              textTransform: "none",
              color: "#fff",
              background: "linear-gradient(135deg, rgba(0, 171, 85, 0.96), rgba(0, 238, 134, 0.95))",
              boxShadow: "0 18px 45px rgba(0, 170, 100, 0.28)",
              '&:hover': {
                transform: "translateY(-2px)",
                boxShadow: "0 24px 55px rgba(0, 170, 100, 0.35)",
              },
            }}
          >
            המשך משחק
          </Button>
        </Stack>
      </Container>

      <Box
        sx={{
          position: "absolute",
          bottom: 24,
          left: "50%",
          transform: "translateX(-50%)",
          width: "92%",
          height: 140,
          borderRadius: 999,
          background: "radial-gradient(ellipse at 50% 40%, rgba(27, 114, 255, 0.22), transparent 55%)",
          filter: "blur(26px)",
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: `linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.03) 45%, transparent 100%)`,
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          top: "20%",
          left: "8%",
          width: 16,
          height: 16,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.65)",
          filter: "blur(1.4px)",
          opacity: 0.9,
          animation: `${flare} 3.8s ease-in-out infinite`,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: "12%",
          right: "10%",
          width: 20,
          height: 20,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.65)",
          filter: "blur(1.8px)",
          opacity: 0.85,
          animation: `${flare} 4.2s ease-in-out infinite`,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "27%",
          right: "18%",
          width: 14,
          height: 14,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.5)",
          filter: "blur(1.1px)",
          opacity: 0.6,
          animation: `${flare} 3.1s ease-in-out infinite`,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: "32%",
          left: "14%",
          width: 26,
          height: 26,
          borderRadius: "50%",
          border: "1px solid rgba(255,255,255,0.22)",
          opacity: 0.35,
          animation: `${float} 5.2s ease-in-out infinite`,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: "55%",
          right: "12%",
          width: 32,
          height: 32,
          borderRadius: "50%",
          border: "1px solid rgba(255,255,255,0.18)",
          opacity: 0.28,
          animation: `${float} 6.4s ease-in-out infinite`,
        }}
      />
    </Box>
  )
}
